import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useStoreData } from '../useStoreData';
import { useLogoMaps } from '../useLogoMaps';
import { fetchDeepLink, openDeepLink } from '../deeplinks';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  TextField,
  Button,
  Tooltip,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  InputAdornment,
  IconButton,
  Popover,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useOddsFormat, fmtPrice } from '../oddsFormat';
import { DataGridPro } from '@mui/x-data-grid-pro';
import '../App.css';
import { TRACKING_API_URL } from '../config';
import { withAuthQuery } from '../auth';
import { bookGroupMenuItems, resolveBooksSelection } from '../bookGroups';
import ArbitrageTutorial from './ArbitrageTutorial';
// ══════════════════════════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════════════════════════

const PREFERRED_BOOK_ORDER = [
  'DraftKings', 'Pinnacle', 'Bookmaker', 'Circa', 'FanDuel', 'Bet365',
  'theScore Bet', 'Caesars', 'BetMGM', 'BetRivers', 'Bovada', 'Hard Rock',
  'Fanatics', 'Prophet Exchange', 'NoVig', 'Polymarket (USA)', 'Kalshi', 'LowVig',
];


// Custom dark theme (same as EVTool.js)
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: 4,
          border: '1px solid #444',
        },
      },
    },
  },
});

// ══════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════

// Helper function to convert American odds to decimal
const americanToDecimal = (american) => {
  if (american > 0) {
    return (american / 100) + 1;
  } else {
    return (100 / Math.abs(american)) + 1;
  }
};

// Helper function to calculate profit from stakes and odds
const calculateProfit = (stake1, stake2, price1, price2) => {
  const decimal1 = americanToDecimal(price1);
  const decimal2 = americanToDecimal(price2);
  const payout1 = stake1 * decimal1;
  const payout2 = stake2 * decimal2;
  const totalStake = stake1 + stake2;
  return Math.min(payout1, payout2) - totalStake;
};

// ROI → color
const roiColor = (roi) => {
  if (roi >= 10) return '#00ff00';
  if (roi >= 5) return '#66ff66';
  if (roi >= 2) return '#99ff99';
  if (roi >= 1) return '#ccffcc';
  return '#ffffff';
};

// Format side abbreviation
const formatLine = (side, points) => {
  const sideAbbr = side === 'Over' ? 'o' : side === 'Under' ? 'u' : '';
  return `${sideAbbr}${points ?? ''}`.trim();
};

// ══════════════════════════════════════════════════════════
//  CELL COMPONENTS
// ══════════════════════════════════════════════════════════

// Direct Deeplink Cell — fetches deep link URL on click via API
const DeeplinkCell = React.memo(({ oddsId, hasDeeplink, isAuthenticated }) => {
  const disabled = !hasDeeplink || !isAuthenticated;

  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <Button
        onClick={async () => {
          if (!hasDeeplink) return;
          const url = await fetchDeepLink(oddsId);
          if (url) openDeepLink(url);
        }}
        disabled={disabled}
        size="small"
        sx={{
          color: disabled ? '#666666' : '#00ff00',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.875rem',
          '&.Mui-disabled': { color: '#666666' },
        }}
      >
        Bet
      </Button>
    </Box>
  );
});

// Hide Arbitrage Cell Component
const HideArbCell = React.memo(({ row, hiddenArbs, toggleHide, showHiddenArbs, isAuthenticated }) => {
  const key = row.arb_key;
  const isHidden = hiddenArbs.includes(key);
  const isDisabled = !isAuthenticated;
  
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) toggleHide(row);
        }}
        size="small"
        sx={{ padding: 0, color: isHidden ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={(isHidden && !showHiddenArbs) || isDisabled}
      >
        {isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </IconButton>
    </Box>
  );
});

// Pin Arbitrage Cell Component
const PinArbCell = React.memo(({ row, pinnedArbs, togglePin, isAuthenticated }) => {
  const isPinned = pinnedArbs.includes(row.arb_key);
  const isDisabled = !isAuthenticated;
  
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) togglePin(row);
        }}
        size="small"
        sx={{ padding: 0, color: isPinned ? '#ff1744' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={isDisabled}
      >
        {isPinned ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,4V9C14,10.12 14.37,11.16 15,12H9C9.65,11.14 10,10.1 10,9V4H14M16,4H17V2H7V4H8V9C8,10.1 7.65,11.16 7,12V14H11V22H13V14H17V12C16.36,11.16 16,10.12 16,9V4Z" opacity="0.5" />
          </svg>
        )}
      </IconButton>
    </Box>
  );
});

// Track Arbitrage Cell Component
const TrackArbCell = React.memo(({ row, trackedBets, toggleTrack, trackingInProgress, isAuthenticated }) => {
  const bet1Tracked = trackedBets && trackedBets.some(bet => bet.oddsId === row.oddsId1 || bet.odds_id === row.oddsId1);
  const bet2Tracked = trackedBets && trackedBets.some(bet => bet.oddsId === row.oddsId2 || bet.odds_id === row.oddsId2);
  const isFullyTracked = bet1Tracked && bet2Tracked;
  const isDisabled = trackingInProgress.has(row.arb_key) || !isAuthenticated;
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => { if (!isDisabled) toggleTrack(row, bet1Tracked, bet2Tracked); }}
        size="small"
        sx={{ padding: 0, color: isFullyTracked ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={isDisabled}
      >
        {isFullyTracked ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>
        )}
      </IconButton>
    </Box>
  );
});

// Editable Stake Cell Component
const EditableStakeCell = React.memo(({ value, rowId, field, onStakeChange }) => {
  const [localValue, setLocalValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const handleFocus = (e) => {
    e.target.select();
    setLocalValue('');
  };

  const handleBlur = () => {
    setIsEditing(false);
    const numValue = parseFloat(localValue);
    if (!isNaN(numValue) && numValue >= 0) {
      const roundedValue = Math.round(numValue * 100) / 100;
      onStakeChange(rowId, field, roundedValue);
    } else {
      setLocalValue(value.toFixed(2));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') e.target.blur();
  };

  if (isEditing) {
    return (
      <TextField
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        size="small"
        type="number"
        inputProps={{ 
          min: 0, 
          step: 0.01,
          style: { MozAppearance: 'textfield', WebkitAppearance: 'none', appearance: 'none' }
        }}
        autoFocus
        sx={{
          width: '85px',
          '& .MuiInputBase-input': { padding: '4px 8px', fontSize: '14px', color: '#ffffff' },
          '& input[type=number]': { MozAppearance: 'textfield' },
          '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
          '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
        }}
      />
    );
  }

  return (
    <Box
      onClick={() => setIsEditing(true)}
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '85px',
      }}
    >
      <Typography variant="body2" sx={{ color: '#ffffff' }}>
        ${value.toFixed(2)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '11px' }}>
        ✎
      </Typography>
    </Box>
  );
});

// ══════════════════════════════════════════════════════════
//  MOBILE CARD
// ══════════════════════════════════════════════════════════

const MobileArbCard = React.memo(({ 
  arb, defaultStake, onStakeChange, isAuthenticated, 
  hiddenArbs, toggleHide, showHiddenArbs, 
  pinnedArbs, togglePin,
  trackedBets, toggleTrack, trackingInProgress
}) => {
  const oddsFormat = useOddsFormat();
  const isPinned = pinnedArbs.includes(arb.arb_key);
  const isHidden = hiddenArbs.includes(arb.arb_key);
  const isHideDisabled = (isHidden && !showHiddenArbs) || !isAuthenticated;
  const bet1Tracked = trackedBets && trackedBets.some(bet => bet.oddsId === arb.oddsId1 || bet.odds_id === arb.oddsId1);
  const bet2Tracked = trackedBets && trackedBets.some(bet => bet.oddsId === arb.oddsId2 || bet.odds_id === arb.oddsId2);
  const isTracked = bet1Tracked && bet2Tracked;
  const isTrackingDisabled = trackingInProgress.has(arb.arb_key) || !isAuthenticated;

  // Parse event info
  const eventDate = arb.eventStart
    ? new Date(arb.eventStart).toLocaleString('en-US', {
        month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
      }).replace(',', '')
    : '';
  
  // Use stakes from arb object (which may have been manually adjusted)
  const stake1 = arb.stake1;
  const stake2 = arb.stake2;
  const guaranteedProfit = arb.actualProfit;
  const totalStake = stake1 + stake2;
  const roi = totalStake > 0 ? (guaranteedProfit / totalStake) * 100 : 0;
  
  const line1 = formatLine(arb.side1, arb.points);
  const line2 = formatLine(arb.side2, arb.points);

  const [editingStake1, setEditingStake1] = useState(false);
  const [editingStake2, setEditingStake2] = useState(false);
  const [tempStake1, setTempStake1] = useState('');
  const [tempStake2, setTempStake2] = useState('');

  const handleStake1Focus = (e) => { e.target.select(); setTempStake1(''); };
  const handleStake2Focus = (e) => { e.target.select(); setTempStake2(''); };

  const handleStake1Change = () => {
    setEditingStake1(false);
    const numValue = parseFloat(tempStake1);
    if (!isNaN(numValue) && numValue >= 0) {
      onStakeChange(arb.arb_key, 'stake1', Math.round(numValue * 100) / 100);
    }
    setTempStake1('');
  };

  const handleStake2Change = () => {
    setEditingStake2(false);
    const numValue = parseFloat(tempStake2);
    if (!isNaN(numValue) && numValue >= 0) {
      onStakeChange(arb.arb_key, 'stake2', Math.round(numValue * 100) / 100);
    }
    setTempStake2('');
  };

  const hasDeepLink1 = !!arb.hasDeeplink1;
  const hasDeepLink2 = !!arb.hasDeeplink2;
  
  return (
    <Card sx={{ 
      mb: 2, 
      border: isPinned ? '2px solid #ff1744' : '1px solid #333',
      backgroundColor: '#1e1e1e',
      borderRadius: 2,
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Profit Header */}
      <Box sx={{ backgroundColor: '#2a2a2a', px: 2, py: 1.5, borderBottom: '1px solid #333' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ color: '#ffffff' }}>
              ${guaranteedProfit.toFixed(2)} Profit
            </Typography>
            <Chip 
              label={`${roi.toFixed(1)}% ROI`}
              size="small"
              sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: roiColor(roi), fontWeight: 500 }}
            />
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => isAuthenticated && togglePin(arb)}
              disabled={!isAuthenticated}
              sx={{ color: isPinned ? '#ff1744' : '#888', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, '&.Mui-disabled': { color: '#444' } }}
            >
              {isPinned ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,4V9C14,10.12 14.37,11.16 15,12H9C9.65,11.14 10,10.1 10,9V4H14M16,4H17V2H7V4H8V9C8,10.1 7.65,11.16 7,12V14H11V22H13V14H17V12C16.36,11.16 16,10.12 16,9V4Z" opacity="0.5" />
                </svg>
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => { if (!isTrackingDisabled) toggleTrack(arb, bet1Tracked, bet2Tracked); }}
              disabled={isTrackingDisabled}
              sx={{ color: isTracked ? '#00ff00' : '#888', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, '&.Mui-disabled': { color: '#444' } }}
            >
              {isTracked ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/></svg>
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => !isHideDisabled && toggleHide(arb)}
              disabled={isHideDisabled}
              sx={{ color: isHidden ? 'green' : '#888', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }, '&.Mui-disabled': { color: '#444' } }}
            >
              {isHidden ? <VisibilityIcon sx={{ fontSize: 20 }} /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Event Info */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" sx={{ color: '#888' }}>{arb.league}</Typography>
            {arb.awayLogo && <img src={arb.awayLogo} alt="" style={{ width: 'auto', height: 14, objectFit: 'contain' }} loading="lazy" />}
            <Typography variant="body2" sx={{ color: '#ffffff' }}>{arb.awayTeam}</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>@</Typography>
            {arb.homeLogo && <img src={arb.homeLogo} alt="" style={{ width: 'auto', height: 14, objectFit: 'contain' }} loading="lazy" />}
            <Typography variant="body2" sx={{ color: '#ffffff' }}>{arb.homeTeam}</Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: '#888' }}>{eventDate}</Typography>
          {arb.playerName && (
            <Typography variant="body2" sx={{ color: '#ffffff', mt: 0.5 }}>
              {arb.playerName} — {arb.betTypeName}
            </Typography>
          )}
        </Box>

        {/* Two bet cards side by side; on a phone each leg needs ~160px, so
            they wrap under each other rather than running off the card */}
        <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {/* Bet 1 */}
          <Box sx={{ flex: '1 1 150px', minWidth: 0, p: 1.5, borderRadius: 1, backgroundColor: '#2a2a2a', border: '1px solid #444' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <Tooltip title={arb.bookName1 || ''} placement="top" arrow>
                <img src={arb.bookLogo1} alt={arb.bookName1} style={{ width: 60, height: 30, objectFit: 'contain' }} loading="lazy" />
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                onClick={async () => { if (arb.hasDeeplink1) { const url = await fetchDeepLink(arb.oddsId1); if (url) openDeepLink(url); } }}
                disabled={!hasDeepLink1 || !isAuthenticated}
                sx={{
                  backgroundColor: hasDeepLink1 && isAuthenticated ? '#00ff00' : '#333',
                  color: hasDeepLink1 && isAuthenticated ? '#000' : '#666',
                  minWidth: 70,
                  '&:hover': { backgroundColor: hasDeepLink1 && isAuthenticated ? '#00cc00' : '#333' },
                  '&.Mui-disabled': { backgroundColor: '#333', color: '#666' }
                }}
              >
                Bet
              </Button>
            </Stack>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff', mb: 0.5 }}>
              {line1} {fmtPrice(arb.price1, oddsFormat)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#ffffff' }}>Stake: $</Typography>
              {editingStake1 ? (
                <TextField
                  value={tempStake1}
                  onChange={(e) => setTempStake1(e.target.value)}
                  onBlur={handleStake1Change}
                  onKeyPress={(e) => e.key === 'Enter' && handleStake1Change()}
                  onFocus={handleStake1Focus}
                  size="small" type="number"
                  inputProps={{ min: 0, step: 0.01, style: { MozAppearance: 'textfield', WebkitAppearance: 'none', appearance: 'none' } }}
                  autoFocus
                  sx={{ width: '70px', '& .MuiInputBase-input': { padding: '2px 6px', fontSize: '12px', color: '#ffffff' }, '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 }, '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}
                />
              ) : (
                <Box onClick={() => setEditingStake1(true)} sx={{ color: '#ffffff', cursor: 'pointer', padding: '2px 6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '2px', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } }}>
                  <Typography variant="caption" sx={{ color: '#ffffff' }}>{stake1.toFixed(2)}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' }}>✎</Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Bet 2 */}
          <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, backgroundColor: '#2a2a2a', border: '1px solid #444' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <Tooltip title={arb.bookName2 || ''} placement="top" arrow>
                <img src={arb.bookLogo2} alt={arb.bookName2} style={{ width: 60, height: 30, objectFit: 'contain' }} loading="lazy" />
              </Tooltip>
              <Button
                variant="contained"
                size="small"
                onClick={async () => { if (arb.hasDeeplink2) { const url = await fetchDeepLink(arb.oddsId2); if (url) openDeepLink(url); } }}
                disabled={!hasDeepLink2 || !isAuthenticated}
                sx={{
                  backgroundColor: hasDeepLink2 && isAuthenticated ? '#00ff00' : '#333',
                  color: hasDeepLink2 && isAuthenticated ? '#000' : '#666',
                  minWidth: 70,
                  '&:hover': { backgroundColor: hasDeepLink2 && isAuthenticated ? '#00cc00' : '#333' },
                  '&.Mui-disabled': { backgroundColor: '#333', color: '#666' }
                }}
              >
                Bet
              </Button>
            </Stack>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff', mb: 0.5 }}>
              {line2} {fmtPrice(arb.price2, oddsFormat)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#ffffff' }}>Stake: $</Typography>
              {editingStake2 ? (
                <TextField
                  value={tempStake2}
                  onChange={(e) => setTempStake2(e.target.value)}
                  onBlur={handleStake2Change}
                  onKeyPress={(e) => e.key === 'Enter' && handleStake2Change()}
                  onFocus={handleStake2Focus}
                  size="small" type="number"
                  inputProps={{ min: 0, step: 0.01, style: { MozAppearance: 'textfield', WebkitAppearance: 'none', appearance: 'none' } }}
                  autoFocus
                  sx={{ width: '70px', '& .MuiInputBase-input': { padding: '2px 6px', fontSize: '12px', color: '#ffffff' }, '& input[type=number]': { MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 }, '& input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}
                />
              ) : (
                <Box onClick={() => setEditingStake2(true)} sx={{ color: '#ffffff', cursor: 'pointer', padding: '2px 6px', borderRadius: '4px', display: 'inline-flex', alignItems: 'center', gap: '2px', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } }}>
                  <Typography variant="caption" sx={{ color: '#ffffff' }}>{stake2.toFixed(2)}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '10px' }}>✎</Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
});

// ══════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════

const ArbDataGrid = ({ username, isAuthenticated = true }) => {
  const oddsFormat = useOddsFormat();
  const arbitrages = useStoreData('arbitrage');
  const betsData = useStoreData('odds');
  const sportsbooks = useStoreData('sportsbooks');
  const leaguesConfig = useStoreData('leagues') || [];
  const { bookLogos, teamMap } = useLogoMaps();
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // ── Build odds lookup map from betsData ──
  const oddsMap = useMemo(() => {
    const map = new Map();
    for (const bet of betsData) {
      map.set(bet.id, bet);
    }
    return map;
  }, [betsData]);

  // ── Enrich arb records with display data from odds ──
  const enrichedArbs = useMemo(() => {
    if (!arbitrages || !arbitrages.length) return [];
    return arbitrages.map(arb => {
      const bet1 = oddsMap.get(arb.odds_id_1);
      const bet2 = oddsMap.get(arb.odds_id_2);
      if (!bet1 || !bet2) return null; // odds not loaded yet

      return {
        // Identity
        arb_key: arb.arb_key,

        // Event display (from either bet — same fixture)
        eventName: `${teamMap[bet1.awayTeamId]?.abbreviation || teamMap[bet1.awayTeamId]?.name} @ ${teamMap[bet1.homeTeamId]?.abbreviation || teamMap[bet1.homeTeamId]?.name}`,
        eventStart: bet1.eventStart,
        league: bet1.league || 'NBA',
        homeTeam: teamMap[bet1.homeTeamId]?.name || '',
        awayTeam: teamMap[bet1.awayTeamId]?.name || '',
        homeLogo: teamMap[bet1.homeTeamId]?.logo || '',
        awayLogo: teamMap[bet1.awayTeamId]?.logo || '',
        homeAbbr: teamMap[bet1.homeTeamId]?.abbreviation || '',
        awayAbbr: teamMap[bet1.awayTeamId]?.abbreviation || '',

        // Bet info
        playerName: bet1.playerName,
        playerId: bet1.playerId || null,
        fixtureId: bet1.fixture_id || null,
        betTypeName: bet1.marketName || bet1.market || '',
        marketId: bet1.market_id || bet1.market || '',
        marketCategory: bet1.marketCategory,
        points: arb.points,

        // Selections (team names for game line arbs)
        selection1: bet1.selection || '',
        selection2: bet2.selection || '',

        // Book 1
        bookName1: bet1.bookName,
        bookLogo1: bookLogos[bet1.bookName] || '',
        side1: bet1.side,
        price1: arb.price_1,
        hasDeeplink1: bet1.hasDeeplink,
        oddsId1: arb.odds_id_1,
        bew1: arb.bew_1,

        // Book 2
        bookName2: bet2.bookName,
        bookLogo2: bookLogos[bet2.bookName] || '',
        side2: bet2.side,
        price2: arb.price_2,
        hasDeeplink2: bet2.hasDeeplink,
        oddsId2: arb.odds_id_2,
        bew2: arb.bew_2,

        // Math (base $100 total stake)
        stake1: arb.stake_1,
        stake2: arb.stake_2,
        guaranteedProfit: arb.guaranteed_profit,
        roiPct: arb.roi_pct,
        bewSum: arb.bew_sum,
      };
    }).filter(Boolean);
  }, [arbitrages, oddsMap, bookLogos, teamMap]);

  // ── Available books: derived from server-provided sportsbooks config (same as Odds/Props) ──
  const availableBooks = useMemo(() => {
    const orderIndex = {};
    PREFERRED_BOOK_ORDER.forEach((name, i) => { orderIndex[name] = i; });
    return sportsbooks.filter(sb => !sb.pickem).map(sb => sb.name).filter(Boolean).sort((a, b) => {
      const ia = orderIndex[a] !== undefined ? orderIndex[a] : 9999;
      const ib = orderIndex[b] !== undefined ? orderIndex[b] : 9999;
      if (ia !== ib) return ia - ib;
      return a.localeCompare(b);
    });
  }, [sportsbooks]);

  // ── Load saved filters and stake from localStorage ──
  const savedLeagueFilter = useMemo(() => {
    try { const s = localStorage.getItem('arbLeagueFilter'); return s ? JSON.parse(s) : []; } catch { return []; }
  }, []);
  const savedBookFilter = useMemo(() => {
    try { const s = localStorage.getItem('arbBookFilter'); return s ? JSON.parse(s) : []; } catch { return []; }
  }, []);
  const savedDefaultStake = useMemo(() => {
    try { const s = localStorage.getItem('arbDefaultStake'); return s ? parseFloat(s) : 1000; } catch { return 1000; }
  }, []);
  const savedShowHiddenArbs = useMemo(() => {
    try { const s = localStorage.getItem('arbShowHiddenArbs'); return s ? JSON.parse(s) : false; } catch { return false; }
  }, []);

  const [leagueFilter, setLeagueFilter] = useState(savedLeagueFilter);
  const [bookFilter, setBookFilter] = useState(savedBookFilter);
  const leagueInitialized = useRef(false);
  const bookInitialized = useRef(false);
  const [defaultStake, setDefaultStake] = useState(savedDefaultStake);
  const [currentPage, setCurrentPage] = useState(0);
  const [manualStakes, setManualStakes] = useState({});
  const [showHiddenArbs, setShowHiddenArbs] = useState(savedShowHiddenArbs);
  const [hiddenArbs, setHiddenArbs] = useState(() => {
    const saved = localStorage.getItem('hiddenArbs');
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.slice(-1000);
  });
  const [pinnedArbs, setPinnedArbs] = useState(() => {
    const saved = localStorage.getItem('pinnedArbs');
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.filter(item => typeof item === 'string').slice(-100);
  });
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [trackingInProgress, setTrackingInProgress] = useState(new Set());
  const [hideTracked, setHideTracked] = useState(() => {
    try { const s = localStorage.getItem('arbHideTracked'); return s ? JSON.parse(s) : false; } catch { return false; }
  });

  // ── Tracked Bets (internal state, backed by REST API) ──
  const [trackedBets, setTrackedBets] = useState([]);

  useEffect(() => {
    if (!username || !isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(withAuthQuery(`${TRACKING_API_URL}?username=${encodeURIComponent(username)}`));
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!cancelled) {
          const bets = (data.tracked_bets || []).map(b => ({ ...b, oddsId: b.odds_id }));
          setTrackedBets(bets);
        }
      } catch (err) {
        console.error('[TRACKED-BETS] Failed to fetch:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [username, isAuthenticated]);

  const apiTrackBet = useCallback(async (row, side) => {
    const isFirst = side === 1;
    const oddsId = isFirst ? row.oddsId1 : row.oddsId2;
    const body = {
      username,
      odds_id:         oddsId,
      sportsbook:      isFirst ? row.bookName1 : row.bookName2,
      sportsbook_logo: isFirst ? row.bookLogo1 : row.bookLogo2,
      fixture_id:      row.fixtureId,
      event_name:      row.eventName,
      event_start:     row.eventStart,
      league:          row.league,
      player_name:     row.playerName,
      player_id:       row.playerId,
      market_id:       row.marketId,
      market_display:  row.betTypeName,
      side:            isFirst ? row.side1 : row.side2,
      points:          row.points,
      price:           isFirst ? row.price1 : row.price2,
      roi:             row.roiPct / 100,
      home_team:       row.homeTeam || '',
      away_team:       row.awayTeam || '',
      home_logo:       row.homeLogo,
      away_logo:       row.awayLogo,
      source_type:     'arbitrage',
      source_key:      row.arb_key,
    };
    const resp = await fetch(withAuthQuery(TRACKING_API_URL), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!resp.ok && resp.status !== 409) throw new Error(`Track failed: HTTP ${resp.status}`);
    setTrackedBets(prev => [...prev, { ...body, oddsId: oddsId }]);
  }, [username]);

  const apiUntrackBet = useCallback(async (oddsId) => {
    const resp = await fetch(
      withAuthQuery(`${TRACKING_API_URL}/${encodeURIComponent(oddsId)}?username=${encodeURIComponent(username)}`),
      { method: 'DELETE' }
    );
    if (!resp.ok && resp.status !== 404) throw new Error(`Untrack failed: HTTP ${resp.status}`);
    setTrackedBets(prev => prev.filter(b => b.oddsId !== oddsId));
  }, [username]);

  const toggleTrack = useCallback(async (row, bet1Tracked, bet2Tracked) => {
    if (!isAuthenticated || !username) return;
    const arbKey = row.arb_key;
    if (trackingInProgress.has(arbKey)) return;
    setTrackingInProgress(prev => new Set(prev).add(arbKey));

    try {
      if (bet1Tracked && bet2Tracked) {
        const promises = [];
        if (row.oddsId1) promises.push(apiUntrackBet(row.oddsId1));
        if (row.oddsId2) promises.push(apiUntrackBet(row.oddsId2));
        await Promise.all(promises);
      } else {
        const promises = [];
        if (!bet1Tracked && row.oddsId1) promises.push(apiTrackBet(row, 1));
        if (!bet2Tracked && row.oddsId2) promises.push(apiTrackBet(row, 2));
        await Promise.all(promises);
      }
    } catch (err) {
      console.error('[TRACK] Error:', err.message);
    } finally {
      setTrackingInProgress(prev => { const next = new Set(prev); next.delete(arbKey); return next; });
    }
  }, [isAuthenticated, username, apiTrackBet, apiUntrackBet, trackingInProgress]);

  // Persist hideTracked
  useEffect(() => { localStorage.setItem('arbHideTracked', JSON.stringify(hideTracked)); }, [hideTracked]);

  // ── Toggle hide ──
  const toggleHide = useCallback((row) => {
    if (!isAuthenticated) return;
    const key = row.arb_key;
    const isHidden = hiddenArbs.includes(key);
    const updated = isHidden ? hiddenArbs.filter(x => x !== key) : [...hiddenArbs, key];
    setHiddenArbs(updated);
    localStorage.setItem('hiddenArbs', JSON.stringify(updated));
  }, [hiddenArbs, isAuthenticated]);

  // ── Toggle pin (arb_key is already a stable composite key) ──
  const togglePin = useCallback((row) => {
    if (!isAuthenticated) return;
    const key = row.arb_key;
    const isPinned = pinnedArbs.includes(key);
    const updated = isPinned
      ? pinnedArbs.filter(x => x !== key)
      : [...pinnedArbs.filter(x => typeof x === 'string'), key];
    setPinnedArbs(updated);
    localStorage.setItem('pinnedArbs', JSON.stringify(updated));
  }, [pinnedArbs, isAuthenticated]);

  // ── Persist filters to localStorage ──
  useEffect(() => { localStorage.setItem('arbLeagueFilter', JSON.stringify(leagueFilter)); }, [leagueFilter]);
  useEffect(() => { localStorage.setItem('arbBookFilter', JSON.stringify(bookFilter)); }, [bookFilter]);
  useEffect(() => { localStorage.setItem('arbDefaultStake', defaultStake.toString()); }, [defaultStake]);
  useEffect(() => { localStorage.setItem('arbShowHiddenArbs', JSON.stringify(showHiddenArbs)); }, [showHiddenArbs]);
  useEffect(() => { localStorage.setItem('pinnedArbs', JSON.stringify(pinnedArbs)); }, [pinnedArbs]);

  // ── Clean up pinnedArbs that no longer exist in dataset ──
  useEffect(() => {
    if (enrichedArbs.length > 0) {
      const currentKeys = new Set(enrichedArbs.map(a => a.arb_key));
      setPinnedArbs(prev => {
        const valid = prev.filter(k => typeof k === 'string' && currentKeys.has(k));
        return valid.length !== prev.length ? valid : prev;
      });
    }
  }, [enrichedArbs]);

  // ── Clean up hiddenArbs that no longer exist ──
  useEffect(() => {
    if (enrichedArbs.length > 0) {
      const currentKeys = new Set(enrichedArbs.map(a => a.arb_key));
      setHiddenArbs(prev => {
        const valid = prev.filter(k => currentKeys.has(k));
        return valid.length !== prev.length ? valid : prev;
      });
    }
  }, [enrichedArbs]);

  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Handle stake changes ──
  const handleStakeChange = (rowId, field, value) => {
    const arb = filteredArbitrages.find(a => a.arb_key === rowId);
    if (!arb) return;

    const decimal1 = americanToDecimal(arb.price1);
    const decimal2 = americanToDecimal(arb.price2);
    let newStake1, newStake2;

    if (field === 'stake1') {
      newStake1 = value;
      newStake2 = value * (decimal1 / decimal2);
    } else {
      newStake2 = value;
      newStake1 = value * (decimal2 / decimal1);
    }

    newStake1 = Math.round(newStake1 * 100) / 100;
    newStake2 = Math.round(newStake2 * 100) / 100;

    setManualStakes(prev => ({ ...prev, [rowId]: { stake1: newStake1, stake2: newStake2 } }));
  };

  const resetStakes = (rowId) => {
    setManualStakes(prev => { const n = { ...prev }; delete n[rowId]; return n; });
  };

  // ── Settings popover ──
  const handleSettingsOpen = useCallback((event) => { setSettingsAnchorEl(event.currentTarget); }, []);
  const handleSettingsClose = useCallback(() => { setSettingsAnchorEl(null); }, []);

  // ══════════════════════════════════════════════════════════
  //  FILTER + SORT + STAKE SCALING
  // ══════════════════════════════════════════════════════════

  const { filterOptions, filteredArbitrages } = useMemo(() => {
    if (!enrichedArbs.length) {
      return { filterOptions: { leagues: [], books: availableBooks }, filteredArbitrages: [] };
    }

    // Build league options: prefer server config, fall back to data-derived
    const leagues = leaguesConfig.length > 0
      ? leaguesConfig.map(l => l.name || l.id).filter(Boolean)
      : [...new Set(enrichedArbs.map(arb => arb.league).filter(Boolean))];

    // Apply filters
    const leagueSet = new Set(leagueFilter);
    const bookSet = new Set(bookFilter);
    const hiddenSet = new Set(hiddenArbs);
    const trackedSet = new Set(trackedBets.map(bet => bet.oddsId || bet.odds_id));
    const filtered = enrichedArbs.filter(arb => {
      if (leagueFilter.length > 0 && arb.league && !leagueSet.has(arb.league)) return false;
      if (bookFilter.length > 0) {
        if (!(arb.bookName1 && bookSet.has(arb.bookName1)) || !(arb.bookName2 && bookSet.has(arb.bookName2))) return false;
      }
      if (!showHiddenArbs && hiddenSet.has(arb.arb_key)) return false;
      if (hideTracked && trackedSet.has(arb.oddsId1) && trackedSet.has(arb.oddsId2)) return false;
      return true;
    });

    // Scale stakes based on defaultStake (base $100 total)
    const baseTotalStake = 100;
    const scaleFactor = defaultStake / baseTotalStake;
    const adjusted = filtered.map(arb => {
      let stake1, stake2;
      if (manualStakes[arb.arb_key]) {
        stake1 = manualStakes[arb.arb_key].stake1;
        stake2 = manualStakes[arb.arb_key].stake2;
      } else {
        stake1 = Math.round(arb.stake1 * scaleFactor * 100) / 100;
        stake2 = Math.round(arb.stake2 * scaleFactor * 100) / 100;
      }
      const actualProfit = calculateProfit(stake1, stake2, arb.price1, arb.price2);
      return { ...arb, stake1, stake2, actualProfit, hasManualStakes: !!manualStakes[arb.arb_key] };
    });

    // Sort by ROI descending
    const sorted = [...adjusted].sort((a, b) => {
      const roiA = (a.stake1 + a.stake2) > 0 ? (a.actualProfit / (a.stake1 + a.stake2)) : 0;
      const roiB = (b.stake1 + b.stake2) > 0 ? (b.actualProfit / (b.stake1 + b.stake2)) : 0;
      return roiB - roiA;
    });

    // Pinned items first, then unpinned
    const pinnedSet = new Set(pinnedArbs.filter(k => typeof k === 'string'));
    const pinnedItems = pinnedArbs
      .filter(k => typeof k === 'string')
      .map(key => sorted.find(a => a.arb_key === key))
      .filter(Boolean);
    const unpinnedItems = sorted.filter(a => !pinnedSet.has(a.arb_key));

    return {
      filterOptions: { leagues, books: availableBooks },
      filteredArbitrages: [...pinnedItems, ...unpinnedItems],
    };
  }, [enrichedArbs, leagueFilter, bookFilter, defaultStake, manualStakes, hiddenArbs, showHiddenArbs, pinnedArbs, availableBooks, trackedBets, hideTracked, leaguesConfig]);

  // ── Initialize filters when options first become available ──
  useEffect(() => {
    if (filterOptions.leagues.length > 0 && !leagueInitialized.current) {
      if (savedLeagueFilter.length > 0) {
        const valid = savedLeagueFilter.filter(l => filterOptions.leagues.includes(l));
        setLeagueFilter(valid.length > 0 ? valid : filterOptions.leagues);
      } else {
        setLeagueFilter(filterOptions.leagues);
      }
      leagueInitialized.current = true;
    }
  }, [filterOptions.leagues, savedLeagueFilter]);

  useEffect(() => {
    if (filterOptions.books.length > 0 && !bookInitialized.current) {
      if (savedBookFilter.length > 0) {
        const valid = savedBookFilter.filter(b => filterOptions.books.includes(b));
        if (valid.length >= 2) {
          setBookFilter(valid);
        } else if (valid.length === 1) {
          const extra = filterOptions.books.find(b => !valid.includes(b));
          setBookFilter([...valid, extra]);
        } else {
          setBookFilter(filterOptions.books.slice(0, 2));
        }
      } else {
        setBookFilter(filterOptions.books);
      }
      bookInitialized.current = true;
    }
  }, [filterOptions.books, savedBookFilter]);

  // ── Filter handlers ──
  const handleFilterChange = (e, options, setter, type) => {
    const val = e.target.value;
    const groupSel = resolveBooksSelection(val, options);
    if (groupSel) {
      // Combo preset: this page requires >=2 books, pad if the group is too small
      setter(type === 'book' && groupSel.length < 2 ? options.slice(0, 2) : groupSel);
      return;
    }
    if (type === 'book' && val.length < 2) {
      if (val.length === 0) { setter(options.slice(0, 2)); }
      else if (val.length === 1) {
        const remaining = options.filter(opt => !val.includes(opt));
        setter([...val, remaining[0]]);
      }
      return;
    }
    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newVal = prev.length === options.length ? [] : options;
        if (type === 'book' && newVal.length < 2) return options.slice(0, 2);
        return newVal;
      });
    } else {
      setter(val);
    }
  };

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple selections (${sel.length})` : sel[0];
  };

  // ── Default stake handlers ──
  const handleDefaultStakeChange = (event) => {
    const value = event.target.value;
    if (value === '') { setDefaultStake(''); }
    else { const n = parseFloat(value); if (!isNaN(n)) setDefaultStake(Math.max(1, n)); }
  };
  const handleDefaultStakeBlur = () => {
    if (defaultStake === '' || defaultStake < 1) setDefaultStake(1);
  };

  // ── Mobile pagination ──
  const mobilePageSize = 5;
  const totalPages = Math.ceil(filteredArbitrages.length / mobilePageSize);
  const startIdx = currentPage * mobilePageSize;
  const paginatedArbitrages = filteredArbitrages.slice(startIdx, startIdx + mobilePageSize);

  useEffect(() => { setCurrentPage(0); }, [leagueFilter, bookFilter, showHiddenArbs, hideTracked]);

  // ══════════════════════════════════════════════════════════
  //  COLUMNS
  // ══════════════════════════════════════════════════════════

  const columns = [
    {
      field: 'pinArb',
      headerName: '',
      sortable: false,
      minWidth: 50, maxWidth: 50, flex: 0,
      headerAlign: 'left', align: 'left',
      renderCell: params => (
        <PinArbCell row={params.row} pinnedArbs={pinnedArbs} togglePin={togglePin} isAuthenticated={isAuthenticated} />
      )
    },
    {
      field: 'eventName',
      headerName: 'Event',
      minWidth: 165, flex: 2,
      headerAlign: 'left', align: 'left',
      renderCell: (params) => {
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', {
              year: '2-digit', month: 'numeric', day: 'numeric',
              hour: 'numeric', minute: '2-digit', hour12: true,
            }).replace(',', '')
          : '';
        const leagueName = params.row.league || '';
        const awayAbbr = params.row.awayAbbr || params.row.awayTeam || '';
        const homeAbbr = params.row.homeAbbr || params.row.homeTeam || '';
        
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {leagueName && (
                <>
                  <Typography sx={{ fontSize: '14px', color: '#ffffff', mr: 0 }}>{leagueName}</Typography>
                  <Typography sx={{ fontSize: '14px', color: '#ffffff', mx: 0.5 }}>|</Typography>
                </>
              )}
              {params.row.awayLogo && (
                <img src={params.row.awayLogo} alt="" style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />
              )}
              <Typography sx={{ fontSize: '14px' }}>{awayAbbr}</Typography>
              <Typography sx={{ fontSize: '12px', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {params.row.homeLogo && (
                <img src={params.row.homeLogo} alt="" style={{ width: 'auto', height: '14px', paddingRight: '2px', objectFit: 'contain' }} loading="lazy" />
              )}
              <Typography sx={{ fontSize: '14px' }}>{homeAbbr}</Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      minWidth: 165, flex: 2,
      headerAlign: 'left', align: 'left',
      renderCell: (params) => {
        const isGameLine = params.row.marketCategory === 'Game Line';
        const betTypeName = params.row.betTypeName || '';
        
        if (isGameLine) {
          // Game line arbs: show selection (team) for each side + market type
          const sel1 = params.row.selection1 || params.row.side1 || '';
          const sel2 = params.row.selection2 || params.row.side2 || '';
          return (
            <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
              <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
                <Tooltip title={`${sel1} — ${betTypeName}`} placement="top" arrow>
                  <Typography sx={{ fontSize: '14px', paddingLeft: '8px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
                    {sel1}
                  </Typography>
                </Tooltip>
              </Box>
              <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
                <Tooltip title={`${sel2} — ${betTypeName}`} placement="top" arrow>
                  <Typography sx={{ fontSize: '14px', paddingLeft: '8px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%', color: '#BBBBBB' }}>
                    {sel2}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          );
        }
        
        // Player prop arbs: show player name + market type
        const playerName = params.row.playerName || '';
        return (
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Tooltip title={playerName} placement="top" arrow>
              <Typography sx={{ fontSize: '14px', paddingLeft: '8px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
                {playerName}
              </Typography>
            </Tooltip>
            <Tooltip title={betTypeName} placement="top" arrow>
              <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
                {betTypeName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'bookLogo1',
      headerName: '',
      minWidth: 26, maxWidth: 26,
      sortable: false,
      headerAlign: 'left', align: 'left',
      renderCell: params => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '50%', padding: 0 }}>
            <Tooltip title={params.row.bookName1 || ''} placement="top" arrow>
              <img src={params.value} alt="Logo 1" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} loading="lazy" />
            </Tooltip>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '50%', padding: 0 }}>
            <Tooltip title={params.row.bookName2 || ''} placement="bottom" arrow>
              <img src={params.row.bookLogo2} alt="Logo 2" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} loading="lazy" />
            </Tooltip>
          </div>
        </Box>
      ),
    },
    {
      field: 'lineOdds',
      headerName: 'Line/Odds',
      minWidth: 130, flex: 1,
      sortable: false,
      headerAlign: 'left', align: 'right',
      renderCell: params => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', paddingRight: '8px' }}>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {formatLine(params.row.side1, params.row.points)}
                </Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {fmtPrice(params.row.price1, oddsFormat)}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {formatLine(params.row.side2, params.row.points)}
                </Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {fmtPrice(params.row.price2, oddsFormat)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'stake1',
      headerName: 'Stake',
      minWidth: 110, flex: 1,
      sortable: false,
      headerAlign: 'left', align: 'left',
      renderCell: params => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', position: 'relative' }}>
          {params.row.hasManualStakes && (
            <Tooltip title="Reset to default stakes">
              <Button
                size="small"
                onClick={() => resetStakes(params.row.arb_key)}
                sx={{ position: 'absolute', top: '2px', right: '10px', minWidth: '20px', padding: '2px', fontSize: '10px', color: '#ff1744', '&:hover': { backgroundColor: 'rgba(255, 23, 68, 0.08)' } }}
              >
                ↺
              </Button>
            </Tooltip>
          )}
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px', paddingLeft: '8px' }}>
            <EditableStakeCell value={params.value} rowId={params.row.arb_key} field="stake1" onStakeChange={handleStakeChange} />
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px', paddingLeft: '8px' }}>
            <EditableStakeCell value={params.row.stake2} rowId={params.row.arb_key} field="stake2" onStakeChange={handleStakeChange} />
          </Box>
        </Box>
      ),
    },
    {
      field: 'actualProfit',
      headerName: 'Profit',
      minWidth: 125, flex: 1,
      headerAlign: 'left', align: 'left',
      sortComparator: (v1, v2, param1, param2) => {
        const ts1 = param1.row.stake1 + param1.row.stake2;
        const ts2 = param2.row.stake1 + param2.row.stake2;
        const roi1 = ts1 > 0 ? (v1 / ts1) : 0;
        const roi2 = ts2 > 0 ? (v2 / ts2) : 0;
        return roi1 - roi2;
      },
      renderCell: (params) => {
        const totalStake = params.row.stake1 + params.row.stake2;
        const roi = totalStake > 0 ? (params.value / totalStake) * 100 : 0;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', paddingLeft: '8px' }}>
            <Typography variant="body2" sx={{ color: '#ffffff', lineHeight: 1.5 }}>
              ${params.value.toFixed(2)}
            </Typography>
            <Typography variant="body2" sx={{ color: roiColor(roi), fontSize: '0.875rem', lineHeight: 'normal' }}>
              {roi.toFixed(1)}% ROI
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'trackArb',
      headerName: 'Track',
      sortable: false,
      minWidth: 65, flex: 1,
      headerAlign: 'left', align: 'left',
      renderCell: params => <TrackArbCell row={params.row} trackedBets={trackedBets} toggleTrack={toggleTrack} trackingInProgress={trackingInProgress} isAuthenticated={isAuthenticated} />
    },
    {
      field: 'hideArb',
      headerName: 'Hide',
      sortable: false,
      minWidth: 65, flex: 1,
      headerAlign: 'left', align: 'left',
      renderCell: params => (
        <HideArbCell row={params.row} hiddenArbs={hiddenArbs} toggleHide={toggleHide} showHiddenArbs={showHiddenArbs} isAuthenticated={isAuthenticated} />
      )
    },
    {
      field: 'deeplinks',
      headerName: '',
      sortable: false,
      minWidth: 80, flex: 1,
      headerAlign: 'left', align: 'left',
      renderCell: params => (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell oddsId={params.row.oddsId1} hasDeeplink={params.row.hasDeeplink1} isAuthenticated={isAuthenticated} />
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell oddsId={params.row.oddsId2} hasDeeplink={params.row.hasDeeplink2} isAuthenticated={isAuthenticated} />
          </Box>
        </Box>
      ),
    },
  ];

  const columnVisibilityModel = {};

  // ══════════════════════════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════════════════════════

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          
          {/* Mobile View */}
          {isMobile ? (
            <>
              {/* Mobile Header */}
              <Box sx={{ position: 'sticky', top: 0, backgroundColor: '#121212', px: 2, py: 1.5, borderBottom: '1px solid #333', zIndex: 10 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="mobile-league-filter-label" sx={{ color: '#ffffff' }}>League</InputLabel>
                      <Select
                        multiple labelId="mobile-league-filter-label" label="League" notched
                        value={leagueFilter}
                        onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                        sx={{ fontSize: '0.875rem' }} disabled={!isAuthenticated}
                        MenuProps={{ autoFocus: false, disableAutoFocusItem: true, PaperProps: { style: { maxHeight: 480 } } }}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.leagues.map(o => (
                          <MenuItem key={o} value={o}><Checkbox checked={leagueFilter.includes(o)} /><ListItemText primary={o} /></MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="mobile-book-filter-label" sx={{ color: '#ffffff' }}>Book</InputLabel>
                      <Select
                        multiple labelId="mobile-book-filter-label" label="Book" notched
                        value={bookFilter}
                        onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
                        sx={{ fontSize: '0.875rem' }} disabled={!isAuthenticated}
                        MenuProps={{ autoFocus: false, disableAutoFocusItem: true, PaperProps: { style: { maxHeight: 480 } } }}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={filterOptions.books.length > 0 && bookFilter.length === filterOptions.books.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {bookGroupMenuItems(filterOptions.books)}
                        {filterOptions.books.map(o => (
                          <MenuItem key={o} value={o}><Checkbox checked={bookFilter.includes(o)} /><ListItemText primary={o} /></MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton 
                      onClick={handleSettingsOpen} disabled={!isAuthenticated}
                      sx={{ color: '#ffffff', '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 } }}
                    >
                      <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  </Stack>
                  <TextField
                    label="Default Stake" type="number" size="small"
                    value={defaultStake}
                    onChange={handleDefaultStakeChange}
                    onBlur={handleDefaultStakeBlur}
                    InputProps={{
                      startAdornment: <Typography sx={{ color: '#ffffff', mr: 1 }}>$</Typography>,
                      inputProps: { min: 1, step: 1 },
                    }}
                    sx={{ width: '100%' }} disabled={!isAuthenticated}
                  />
                </Stack>
              </Box>

              {/* Mobile Content */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, paddingBottom: totalPages > 1 ? '80px' : '16px' }}>
                {filteredArbitrages.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
                    <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>
                      No arbitrages available, please check back later
                    </Typography>
                  </Box>
                ) : (
                  paginatedArbitrages.map(arb => (
                    <MobileArbCard 
                      key={arb.arb_key} 
                      arb={arb} 
                      defaultStake={defaultStake}
                      onStakeChange={handleStakeChange}
                      isAuthenticated={isAuthenticated}
                      hiddenArbs={hiddenArbs}
                      toggleHide={toggleHide}
                      showHiddenArbs={showHiddenArbs}
                      pinnedArbs={pinnedArbs}
                      togglePin={togglePin}
                      trackedBets={trackedBets}
                      toggleTrack={toggleTrack}
                      trackingInProgress={trackingInProgress}
                    />
                  ))
                )}
              </Box>

              {/* Mobile Pagination */}
              {totalPages > 1 && (
                <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#1e1e1e', borderTop: '1px solid #333', p: 2, zIndex: 9 }}>
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                    <Button size="small" onClick={() => setCurrentPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0 || !isAuthenticated} sx={{ color: '#ffffff' }}>Previous</Button>
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>{currentPage + 1} / {totalPages}</Typography>
                    <Button size="small" onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage >= totalPages - 1 || !isAuthenticated} sx={{ color: '#ffffff' }}>Next</Button>
                  </Stack>
                </Box>
              )}
            </>
          ) : (
            // Desktop View
            <>
              <Box className="filter-section">
                <ArbitrageTutorial />
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="league-filter-label" sx={{ color: '#ffffff' }}>League</InputLabel>
                      <Select
                        multiple labelId="league-filter-label" label="League" notched
                        value={leagueFilter}
                        onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                        disabled={!isAuthenticated}
                        MenuProps={{ autoFocus: false, disableAutoFocusItem: true, PaperProps: { style: { maxHeight: 480 } } }}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.leagues.map(o => (
                          <MenuItem key={o} value={o}><Checkbox checked={leagueFilter.includes(o)} /><ListItemText primary={o} /></MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="book-filter-label" sx={{ color: '#ffffff' }}>Book</InputLabel>
                      <Select
                        multiple labelId="book-filter-label" label="Book" notched
                        value={bookFilter}
                        onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
                        disabled={!isAuthenticated}
                        MenuProps={{ autoFocus: false, disableAutoFocusItem: true, PaperProps: { style: { maxHeight: 480 } } }}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={filterOptions.books.length > 0 && bookFilter.length === filterOptions.books.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {bookGroupMenuItems(filterOptions.books)}
                        {filterOptions.books.map(o => (
                          <MenuItem key={o} value={o}><Checkbox checked={bookFilter.includes(o)} /><ListItemText primary={o} /></MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Default Stake" type="number" size="small"
                      value={defaultStake}
                      onChange={handleDefaultStakeChange}
                      onBlur={handleDefaultStakeBlur}
                      InputProps={{
                        startAdornment: <Typography sx={{ color: '#ffffff', mr: 1 }}>$</Typography>,
                        inputProps: { min: 1, step: 1 },
                      }}
                      sx={{ width: 150 }} disabled={!isAuthenticated}
                    />
                  </Stack>
                  <IconButton 
                    onClick={handleSettingsOpen} disabled={!isAuthenticated}
                    data-tutorial="arb-settings-gear"
                    sx={{ color: '#ffffff', '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 } }}
                  >
                    <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Stack>
              </Box>
              <Box className="table-section">
                <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
                  {filteredArbitrages.length === 0 ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>
                        No arbitrages available, please check back later
                      </Typography>
                    </Box>
                  ) : (
                    <DataGridPro
                      rows={filteredArbitrages}
                      columns={columns}
                      getRowId={row => row.arb_key}
                      getRowClassName={(params) => pinnedArbs.includes(params.row.arb_key) ? 'pinned-row' : ''}
                      sortingMode="client"
                      disableSelectionOnClick
                      hideFooter
                      columnVisibilityModel={columnVisibilityModel}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#1e1e1e', borderBottom: '2px solid #444',
                          position: 'sticky', top: '56px', zIndex: 2,
                        },
                        '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-cell': {
                          color: '#ffffff !important', userSelect: 'none',
                          borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                        },
                        '& .MuiDataGrid-cell[data-field="bookLogo1"]': { padding: 0 },
                        '& .MuiDataGrid-cell:last-child': { borderRight: 'none' },
                        '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row': {
                          position: 'relative',
                          '&:hover': { backgroundColor: '#1a1a1a' },
                          '&.pinned-row': {
                            backgroundColor: 'rgba(255, 23, 68, 0.08)',
                            '&:hover': { backgroundColor: 'rgba(255, 23, 68, 0.12)' },
                            '&::before': {
                              content: '""', position: 'absolute',
                              left: 0, top: 0, bottom: 0, width: '3px',
                              backgroundColor: '#ff1744',
                            }
                          }
                        },
                        '& .MuiDataGrid-row.Mui-selected': {
                          backgroundColor: '#1a1a1a', '&:hover': { backgroundColor: '#1a1a1a' },
                        },
                        '& .MuiDataGrid-columnSeparator': { visibility: 'visible', color: 'rgba(255, 255, 255, 0.3)' },
                        '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: '#ffffff !important', whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.2,
                        },
                        '& .MuiDataGrid-root': { overflowY: 'auto !important', margin: 0, boxSizing: 'border-box' },
                        '& .MuiDataGrid-virtualScroller': { overflowY: 'auto !important' },
                        '& .MuiDataGrid-main': { padding: 0, boxSizing: 'border-box' },
                        '& .MuiDataGrid-footerContainer': {
                          backgroundColor: '#1e1e1e', color: '#ffffff !important',
                          position: 'sticky', bottom: 0, zIndex: 1, boxSizing: 'border-box',
                        },
                      }}
                      rowBuffer={5}
                      rowThreshold={2}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}

          {/* Settings Popover */}
          <Popover
            open={Boolean(settingsAnchorEl)}
            anchorEl={settingsAnchorEl}
            onClose={handleSettingsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{ zIndex: 1300 }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Stack direction="column" spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideTracked}
                      onChange={(e) => setHideTracked(e.target.checked)}
                      sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                      disabled={!isAuthenticated}
                    />
                  }
                  label="Hide Tracked Arbs"
                  sx={{ color: '#ffffff' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showHiddenArbs}
                      onChange={(e) => setShowHiddenArbs(e.target.checked)}
                      sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                      disabled={!isAuthenticated}
                    />
                  }
                  label="Show Hidden Arbs"
                  sx={{ color: '#ffffff', mb: 2 }}
                />
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={handleSettingsClose} sx={{ color: '#ffffff' }}>Cancel</Button>
                <Button size="small" onClick={handleSettingsClose} variant="contained" sx={{ backgroundColor: '#ff1744', '&:hover': { backgroundColor: '#d81b60' } }}>Save</Button>
              </Stack>
            </Box>
          </Popover>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default ArbDataGrid;